import List from '@pelckmans/elementis/components/list';
import ChapterIcon from '@pelckmans/elementis/icons/chapter';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ModuleContext from '../../_contexts/ModuleContext';
import { SearchResultUserMaterial } from '../api/search-api';
import ModalContext from '../../../../contexts/ModalContext';
import Pill from '@pelckmans/elementis/components/pill';
import { getFullHierarchyDescriptionOfNode } from '../../../../utils/getNodeDescription';
import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { getSharedUserMaterial, getUserMaterialById } from '../../../../api/user-material';
import useUserMaterial from '../../toc/hooks/user-material/useUserMaterial';
import { buildGroupProps } from '../../../../utils/elementisUtils';
import useLicenses from '../../../../hooks/useLicenses';
import stripHtml from '../../../../utils/wordlist/stripHtml';
import IdentityContext from '../../../../contexts/IdentityContext';

interface Props {
  searchItem: SearchResultUserMaterial;
}

function SearchResultUserMaterialItem({ searchItem }: Props) {
  const { allModules } = useContext(ModuleContext);
  const { showModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const { openMaterial } = useUserMaterial();
  const { isModuleLicensed } = useLicenses();

  const {
    identity: { userId },
  } = useContext(IdentityContext);

  const targetModule = allModules.find(m => m.id === searchItem.moduleId);

  const handleOpenUserMaterial = useCallback(
    async (searchItem: SearchResultUserMaterial) => {
      const userMaterial = searchItem.owner
        ? (await getSharedUserMaterial(searchItem.moduleId, { includeScore: true, ids: [searchItem.id], limit: 1 }))
            .data[0]
        : await getUserMaterialById(searchItem.moduleId, searchItem.id);
      if (!userMaterial || !targetModule) return;

      const modal = await openMaterial(userMaterial, searchItem.hierarchy);
      if (modal) {
        showModal(modal);
      }
    },
    [targetModule, openMaterial, showModal],
  );

  const content = searchItem.content ? searchItem.content : undefined;

  if (!targetModule) return null;

  const licensedModule = isModuleLicensed(targetModule);

  if (searchItem.kind === 'link') {
    return (
      <List.Item
        key={searchItem.id}
        as="link"
        title={!licensedModule ? stripHtml(searchItem.name) : searchItem.name}
        href={searchItem.href as string}
        target="_blank"
        disabled={!licensedModule}
        pillGroupProps={buildGroupProps(
          <>
            <Pill key={'scope-pill'}>{t('PAGES.SEARCH.RESULTS.MATERIAL')}</Pill>
            <Pill
              key="module-pill"
              size="sm"
              avatar={
                <Avatar>
                  <AvatarImage size="xs" name={targetModule!.niceName} src={targetModule!.styling?.logo || ''} />
                </Avatar>
              }
            >
              {targetModule?.niceName}
            </Pill>
            <Pill key="hierarchy-pill" icon={<ChapterIcon />}>
              {getFullHierarchyDescriptionOfNode(searchItem.hierarchy, t)}
            </Pill>
            {!searchItem.sharedWithUser && searchItem.owner === userId && (
              <Pill key="content-type-pill">{t('CONTENT_TYPE_DESCRIPTIONS.OWN_MATERIAL')}</Pill>
            )}
            {searchItem.sharedWithUser && (
              <Pill key="content-type-pill">{t('CONTENT_TYPE_DESCRIPTIONS.SHARED_MATERIAL')}</Pill>
            )}
          </>,
        )}
      />
    );
  }

  return (
    <List.Item
      key={searchItem.id}
      as="button"
      title={!licensedModule ? stripHtml(searchItem.name) : searchItem.name}
      subText={content}
      onClick={() => {
        handleOpenUserMaterial(searchItem);
      }}
      disabled={!licensedModule}
      pillGroupProps={{
        'aria-label': undefined,
        children: (
          <>
            <Pill key={'scope-pill'}>{t('PAGES.SEARCH.RESULTS.MATERIAL')}</Pill>
            <Pill
              key="module-pill"
              size="sm"
              avatar={
                <Avatar>
                  <AvatarImage size="xs" name={targetModule!.niceName} src={targetModule!.styling?.logo || ''} />
                </Avatar>
              }
            >
              {targetModule?.niceName}
            </Pill>
            <Pill key="hierarchy-pill" icon={<ChapterIcon />}>
              {getFullHierarchyDescriptionOfNode(searchItem.hierarchy, t)}
            </Pill>
            {!searchItem.sharedWithUser && searchItem.owner === userId && (
              <Pill key="content-type-pill">{t('CONTENT_TYPE_DESCRIPTIONS.OWN_MATERIAL')}</Pill>
            )}
            {searchItem.sharedWithUser && (
              <Pill key="content-type-pill">{t('CONTENT_TYPE_DESCRIPTIONS.SHARED_MATERIAL')}</Pill>
            )}
          </>
        ),
        role: 'group',
      }}
    />
  );
}

export default SearchResultUserMaterialItem;
